/**
 * 통합관리자 — 기업별 정산(거래 원장)
 * ---------------------------------------------------------------------------
 * ## 왜 표를 셋으로 나누지 않았나
 * 지금까지 운영자는 「청구서」, 「수납」, 「환불」을 **따로** 봤다. 그래서
 * "이 입금이 어느 청구서 것인가", "그 환불은 갚았나" 를 사람이 머리로
 * 맞춰야 했다. 기업이 늘면 못 맞춘다.
 *
 * 한 기업의 돈 흐름을 **시간순 한 줄기**로 편다.
 *
 *   10/01  청구  +36,300   잔액 36,300
 *   10/05  수납  −36,000   잔액    300
 *   10/20  환불    +300    잔액    600   ← 돌려줄 돈이 생겼다
 *   11/01  차감    −300    잔액    300   ← 다음 청구서에서 갚았다
 *
 * 부호는 **「우리가 받을 돈」 기준**이다. 청구는 늘고, 수납은 줄고, 환불은
 * (갚아야 하므로) 늘고, 차감은 줄어든다. 마지막 잔액 하나로 "지금 이
 * 기업에서 받을 돈" 이 읽힌다.
 *
 * ## 왜 별도 메뉴가 아니라 서랍인가
 * 이 화면이 필요해지는 순간은 늘 **다른 목록을 보다가**다 — 연체가 눈에
 * 띄었거나, 환불이 안 갚아진 것을 봤거나. 메뉴로 빼면 기업을 다시 찾아
 * 들어가야 한다. 어디서든 기업 이름을 누르면 그 자리에서 열린다.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Col,
  Descriptions,
  Drawer,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

type EntryKind = 'INVOICE' | 'PAYMENT' | 'REFUND' | 'CREDIT';

interface LedgerEntry {
  at: string;
  kind: EntryKind;
  label: string;
  delta: number;
  balance: number;
  invoice_id: string | null;
  payment_id: string | null;
  refund_id: string | null;
  note: string | null;
  status: string;
}

interface Ledger {
  tenantId: string;
  companyName: string;
  slug: string;
  status: string;
  invoiceMode: 'AUTO' | 'MANUAL';
  dueDays: number;
  summary: {
    billed: number;
    paid: number;
    refunded: number;
    credited: number;
    unpaid: number;
    pending_refund: number;
  };
  entries: LedgerEntry[];
}

const KIND: Record<EntryKind, { text: string; color: string }> = {
  INVOICE: { text: '청구', color: 'blue' },
  PAYMENT: { text: '수납', color: 'green' },
  REFUND: { text: '환불', color: 'orange' },
  CREDIT: { text: '차감', color: 'purple' },
};

const won = (v: number | null | undefined) =>
  `${Math.round(Number(v ?? 0)).toLocaleString('ko-KR')}원`;

/** 부호를 붙여 보여 준다. 받을 돈이 느는지 주는지가 한눈에 보여야 한다 */
const signed = (v: number) =>
  `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Math.round(v)).toLocaleString('ko-KR')}원`;

export function TenantLedgerDrawer({
  tenantId,
  onClose,
}: {
  tenantId: string | null;
  onClose: () => void;
}) {
  const ledger = useQuery<Ledger, NormalizedError>({
    queryKey: ['admin', 'billing', 'ledger', tenantId],
    queryFn: async () => (await apiClient.get(`/admin/billing/ledger/${tenantId}`)).data,
    enabled: Boolean(tenantId),
  });

  const d = ledger.data;

  const columns: ColumnsType<LedgerEntry> = [
    {
      title: '날짜',
      key: 'at',
      width: 150,
      render: (_, r) => dayjs(r.at).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '구분',
      key: 'kind',
      width: 80,
      render: (_, r) => <Tag color={KIND[r.kind].color}>{KIND[r.kind].text}</Tag>,
    },
    {
      title: '내용',
      key: 'label',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.label}</Text>
          {r.note && (
            <Text type="secondary" style={{ fontSize: 12 }}>{r.note}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '증감',
      key: 'delta',
      width: 120,
      align: 'right',
      render: (_, r) => (
        <Text type={r.delta > 0 ? undefined : 'success'}>{signed(r.delta)}</Text>
      ),
    },
    {
      title: '받을 돈',
      key: 'balance',
      width: 120,
      align: 'right',
      render: (_, r) => <Text strong>{won(r.balance)}</Text>,
    },
  ];

  return (
    <Drawer
      open={Boolean(tenantId)}
      onClose={onClose}
      width={900}
      title={d ? `${d.companyName} — 거래 원장` : '거래 원장'}
      destroyOnClose
    >
      {ledger.isError && <Alert type="error" showIcon message={errorText(ledger.error)} />}

      {d && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions size="small" column={3} bordered>
            <Descriptions.Item label="식별자">{d.slug}</Descriptions.Item>
            <Descriptions.Item label="청구 방식">
              <Tag color={d.invoiceMode === 'AUTO' ? 'blue' : 'default'}>
                {d.invoiceMode === 'AUTO' ? '자동' : '수동'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="납기">{d.dueDays}일</Descriptions.Item>
          </Descriptions>

          <Row gutter={12}>
            <Col span={8}>
              <Statistic title="청구 합계" value={d.summary.billed} suffix="원" />
            </Col>
            <Col span={8}>
              <Statistic title="수납 합계" value={d.summary.paid} suffix="원"
                valueStyle={{ color: '#3f8600' }} />
            </Col>
            <Col span={8}>
              <Statistic title="미수금" value={d.summary.unpaid} suffix="원"
                valueStyle={{ color: d.summary.unpaid > 0 ? '#cf1322' : undefined }} />
            </Col>
            <Col span={8}>
              <Statistic title="환불 합계" value={d.summary.refunded} suffix="원" />
            </Col>
            <Col span={8}>
              <Statistic title="청구서에서 갚은 금액" value={d.summary.credited} suffix="원" />
            </Col>
            <Col span={8}>
              <Statistic title="아직 못 갚은 환불" value={d.summary.pending_refund} suffix="원"
                valueStyle={{ color: d.summary.pending_refund > 0 ? '#d46b08' : undefined }} />
            </Col>
          </Row>

          <Table<LedgerEntry>
            rowKey={(r) => `${r.kind}-${r.at}-${r.invoice_id ?? ''}-${r.payment_id ?? ''}-${r.refund_id ?? ''}`}
            size="small"
            loading={ledger.isLoading}
            dataSource={d.entries}
            columns={columns}
            pagination={{ pageSize: 30, hideOnSinglePage: true }}
            locale={{ emptyText: '거래 내역이 없습니다.' }}
          />

          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            「받을 돈」은 그 시점까지의 누계입니다 — 청구는 더하고, 수납은 뺍니다. 환불은
            <b> 돌려줘야 할 돈</b>이라 더하고, 다음 청구서에서 깎아 갚으면 뺍니다.
            무효화한 청구서는 세지 않습니다.
          </Paragraph>
        </Space>
      )}
    </Drawer>
  );
}
